import { useEffect, useState } from 'react';
import {
  CAPPED_RULES, MAX_MULTIPLIER, RANGELESS_RULES, digitRange, valueRange,
  type LevelConfig,
} from '@numslearn/shared';
import { useSettings } from '../settings.js';
import type { Translate } from '../i18n/index.js';
import { Field, Segmented, Select } from './controls.js';

/**
 * Level settings shared by the teacher panel and the curriculum editor, so the
 * two cannot drift into describing the same field differently.
 */

type Patch = (p: Partial<LevelConfig>) => void;

/**
 * One bound of the range.
 *
 * A plain number box, committed on blur or Enter rather than per keystroke:
 * typing "15" passes through "1", and committing that would drag the other
 * bound with it.
 */
function BoundField({ label, value, onCommit }: { label: string; value: number; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const n = Math.round(Number(draft));
    if (draft.trim() === '' || !Number.isFinite(n)) { setDraft(String(value)); return; }
    const v = Math.max(0, Math.min(9999, n));
    setDraft(String(v));
    if (v !== value) onCommit(v);
  };
  return (
    <Field label={label}>
      <input
        className="a-select a-bound" type="number" inputMode="numeric" min={0} max={9999}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      />
    </Field>
  );
}

/** Why the current range will not deal the board the teacher expects, if it will not. */
function rangeWarning(c: LevelConfig, t: Translate, num: (n: number | string) => string): string | null {
  const [lo, hi] = valueRange(c);
  const n = c.ruleTarget;
  switch (c.rule) {
    case 'sumTo':
    case 'sumTo20':
      return Math.max(lo, n - hi) > Math.min(hi, n - lo) ? t('admin.warnSum', { t: num(n) }) : null;
    case 'productTo': {
      for (let a = 1; a * a <= n; a++) {
        if (n % a === 0 && a >= lo && a <= hi && n / a >= lo && n / a <= hi) return null;
      }
      return t('admin.warnProduct', { t: num(n) });
    }
    case 'multipleOf':
      return Math.max(lo, c.allowOneAsFactor ? 1 : 2) * 2 > hi ? t('admin.warnMultiple') : null;
    case 'multiplesOf': {
      if (n <= 0) return null;
      if (Math.floor(hi / n) * n < Math.max(lo, 1)) return t('admin.warnNoMultiples', { n: num(n) });
      const top = n * c.maxMultiplier;
      return c.maxMultiplier > 0 && hi < top
        ? t('admin.warnMultiplesCap', { n: num(n), k: num(c.maxMultiplier), x: num(top) })
        : null;
    }
    case 'factorsOf': {
      for (let v = Math.max(1, lo); v <= Math.min(hi, n); v++) if (n % v === 0) return null;
      return t('admin.warnFactors', { n: num(n) });
    }
    default:
      return null;
  }
}

/**
 * "Numbers from … to …", plus one-tap digit presets.
 *
 * It always shows the range the board will actually use — the old fields
 * showed 1 and 9 for a level that was really running on its digit count — and
 * a preset sets the digits and both bounds together, so the two can no longer
 * disagree.
 */
export function RangeFields({ config, onChange }: { config: LevelConfig; onChange: Patch }) {
  const { t, num } = useSettings();
  if (RANGELESS_RULES.has(config.rule)) return null;
  const [lo, hi] = valueRange(config);
  const presets = [1, 2, 3, 4].map((d) => ({ d, range: digitRange(d) }));
  const active = presets.find((p) => p.range[0] === lo && p.range[1] === hi);
  const warning = rangeWarning(config, t, num);

  return (
    <div className="a-range">
      <div className="a-row">
        <BoundField
          label={t('admin.rangeFrom')} value={lo}
          onCommit={(v) => onChange({ valueMin: v, valueMax: Math.max(v, hi) })}
        />
        <BoundField
          label={t('admin.rangeTo')} value={hi}
          onCommit={(v) => onChange({ valueMax: Math.max(1, v), valueMin: Math.min(v, lo) })}
        />
      </div>
      <Segmented<string>
        label={t('admin.rangeQuick')}
        value={active ? String(active.d) : ''}
        onChange={(v) => {
          const d = Number(v);
          const [a, b] = digitRange(d);
          onChange({ digits: d, valueMin: a, valueMax: b });
        }}
        options={presets.map((p) => ({ value: String(p.d), label: `${num(p.range[0])}–${num(p.range[1])}` }))}
      />
      {warning && <p className="a-warn" role="status">{warning}</p>}
    </div>
  );
}

/**
 * "Multiplied by up to": × 2 … × 12, or no limit.
 *
 * On a multiples hunt the largest number is raised to N × k when it falls
 * short, so picking "up to × 10" actually puts 10N on the board rather than
 * silently stopping wherever the old range ended.
 */
export function MultiplierField({ config, onChange }: { config: LevelConfig; onChange: Patch }) {
  const { t, num } = useSettings();
  if (!CAPPED_RULES.has(config.rule)) return null;
  const options = [{ value: 0, label: t('admin.noLimit') }];
  for (let k = 2; k <= MAX_MULTIPLIER; k++) options.push({ value: k, label: `× ${num(k)}` });

  return (
    <Select<number>
      label={t('admin.maxMultiplier')}
      hint={t('admin.maxMultiplierHelp')}
      value={config.maxMultiplier}
      options={options}
      onChange={(k) => {
        const [, hi] = valueRange(config);
        const top = config.ruleTarget * k;
        const widen = config.rule === 'multiplesOf' && k > 0 && hi < top;
        onChange({ maxMultiplier: k, ...(widen ? { valueMax: top } : {}) });
      }}
    />
  );
}
